import _ from 'lodash'
import templateUrl from './placesTree.html'
import templateNested from './placesTree.nested.html'

const defaultMaxPlaces = 200

/**
 * A form widget that displays a nested tree of places grouped by their hierarchies.
 * If there more than 200 places, a faster autocomplete is rendered instead.
 *
 * @example
 * <tmr-places-tree
 *  label=""
 *  places=""
 *  filters=""
 *  max-places=""
 *  on-places-change
 * ></tmr-places-tree>
 *
 * @type {ngComponent}
 * @prop {string} label Translation key of the field label
 * @prop {object=} filters Optional filters for the places query
 * @prop {number=} [maxPlaces=200] Maximim number of places for the tree widget. If there are more places, a simple autocomplete is used instead
 * @prop {array<Place>} places The list of selected places
 */
export const tmrPlacesTree = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrPlacesTreeController',
  bindings: {
    label: '@',
    filters: '<',
    maxPlaces: '<',
    places: '<',
    onPlacesChange: '&',
  },
}

/**
 * Controller of component {@link tmrPlacesTree}.
 *
 * @type {ngController}
 */
export class tmrPlacesTreeController {
  static ngType = 'controller'

  constructor(Hierarchy, Place) {
    'ngInject'
    Object.assign(this, { Hierarchy, Place, templateNested })
  }

  $onInit() {
    this.maxPlaces = this.maxPlaces ?? defaultMaxPlaces
    this.Place.count(this.filters).then(placesCount =>
      this.onPlacesCountLoaded(placesCount)
    )
  }

  onPlacesCountLoaded(placesCount) {
    this.placesCount = placesCount
    if (placesCount && placesCount <= this.maxPlaces) {
      this.allPlaces = this.Place.query(this.filters)
      this.hierarchies = this.Hierarchy.query()
      Promise.all([this.allPlaces.$promise, this.hierarchies.$promise]).then(
        () => this.onLoaded()
      )
    }
  }

  onLoaded() {
    // Default places. Keys are ids, values are true (i.e.: checked).
    this.placesModels = {}
    this.places.forEach(({ id }) => (this.placesModels[id] = true))

    // Build a tree with of hierarchies and places, then remove branches without places.
    this.allPlaces.forEach(place => (place.parent = place.hierarchy))
    let filterTree = tree => {
      tree.forEach(branch => (branch.children = filterTree(branch.children))) // Bottom up recursion
      return tree.filter(
        branch => branch.$getType() == 'Place' || branch.children.length > 0
      )
    }
    this.placesTree = filterTree(
      _.tree(this.hierarchies.concat(this.allPlaces))
    )
  }

  /** Toggles clicked hierarchy */
  toggleHierarchy(hierarchy, expand) {
    this.toggleHierarchyBranch(
      hierarchy.children,
      !this.isHierarchyChecked(hierarchy)
    )
    expand()
  }

  /** Checks if a hierarchy is checked */
  isHierarchyChecked({ children }) {
    return children.every(
      child =>
        (child.$getType() == 'Place' && this.placesModels[child.id]) ||
        (child.$getType() == 'Hierarchy' && this.isHierarchyChecked(child))
    )
  }

  /** Toggles an entire branch of hierarchies and places */
  toggleHierarchyBranch(children, check) {
    children.forEach(child => {
      if (child.$getType() == 'Place') {
        this.placesModels[child.id] = check
        this.togglePlace(child)
      } else this.toggleHierarchyBranch(child.children, check)
    })
  }

  /** Checks if a hierarchy branch checkbox is indeterminate */
  hierarchyIsIndeterminate(hierarchy) {
    let childPlaces = []
    let findChildPlaces = children => {
      childPlaces.push(...children.filter(child => child.$getType() == 'Place'))
      children.forEach(child => findChildPlaces(child.children))
    }
    findChildPlaces(hierarchy.children)
    let checkedPlaces = childPlaces.filter(
      ({ id }) => this.placesModels[id] === true
    )
    return checkedPlaces.length > 0 && checkedPlaces.length < childPlaces.length
  }

  /** Adds a place or removes it from the list of selected places */
  togglePlace(place) {
    if (this.placesModels[place.id]) this.places.push(place)
    else {
      let idx = this.places.findIndex(({ id }) => id == place.id)
      if (idx > -1) this.places.splice(idx, 1)
    }
  }
}
